import type {
  MastraEvent,
  ModelStep,
  RawEventRecord,
  RunState,
  ToolCall,
  TraceEntry,
  UsageMetrics,
} from './types';

type UnknownRecord = Record<string, unknown>;

const SECRET_KEY = /(authorization|cookie|api[-_]?key|access[-_]?token|refresh[-_]?token|password|secret|credential)/i;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): UnknownRecord {
  return isRecord(value) ? value : {};
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function nested(record: UnknownRecord, ...keys: string[]): unknown {
  let value: unknown = record;
  for (const key of keys) value = asRecord(value)[key];
  return value;
}

export function sanitizeForDisplay(value: unknown, depth = 0): unknown {
  if (depth > 32) return '[depth limit]';
  if (Array.isArray(value)) return value.map((item) => sanitizeForDisplay(item, depth + 1));
  if (!isRecord(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      key,
      SECRET_KEY.test(key) ? '[redacted]' : sanitizeForDisplay(child, depth + 1),
    ]),
  );
}

export function createRun(input: {
  id?: string;
  agentId: string;
  mode: 'stream' | 'network';
  prompt: string;
  threadId: string;
  startedAt?: number;
}): RunState {
  const startedAt = input.startedAt ?? Date.now();
  return {
    id: input.id ?? crypto.randomUUID(),
    agentId: input.agentId,
    mode: input.mode,
    prompt: input.prompt,
    threadId: input.threadId,
    status: 'streaming',
    startedAt,
    answer: '',
    reasoning: '',
    trace: [
      {
        id: crypto.randomUUID(),
        kind: 'system',
        label: 'Request sent',
        detail: input.mode === 'network' ? 'Network execution' : 'Agent stream',
        status: 'complete',
        atMs: 0,
      },
    ],
    tools: [],
    steps: [],
    eventCounts: {},
    totalEvents: 0,
    eventLog: [],
  };
}

export function usageFrom(value: unknown): UsageMetrics | undefined {
  const source = asRecord(value);
  if (Object.keys(source).length === 0) return undefined;

  const inputTokens = asNumber(source.inputTokens) ?? asNumber(source.promptTokens);
  const outputTokens = asNumber(source.outputTokens) ?? asNumber(source.completionTokens);
  const totalTokens = asNumber(source.totalTokens)
    ?? (inputTokens !== undefined && outputTokens !== undefined ? inputTokens + outputTokens : undefined);
  const completionDetails = asRecord(source.completionTokensDetails);
  const promptDetails = asRecord(source.promptTokensDetails);
  const metrics: UsageMetrics = {
    inputTokens,
    outputTokens,
    totalTokens,
    reasoningTokens: asNumber(source.reasoningTokens) ?? asNumber(completionDetails.reasoningTokens),
    cachedInputTokens: asNumber(source.cachedInputTokens) ?? asNumber(promptDetails.cachedTokens),
    cacheCreationInputTokens: asNumber(source.cacheCreationInputTokens) ?? asNumber(promptDetails.cacheWriteTokens),
    cost: asNumber(source.cost)
      ?? asNumber(nested(source, 'raw', 'cost'))
      ?? asNumber(nested(source, 'raw', 'raw', 'cost')),
  };

  return Object.values(metrics).some((item) => item !== undefined) ? metrics : undefined;
}

function usageFromPayload(payload: UnknownRecord): UsageMetrics | undefined {
  return usageFrom(payload.usage)
    ?? usageFrom(payload.totalUsage)
    ?? usageFrom(nested(payload, 'metadata', 'providerMetadata', 'openrouter', 'usage'))
    ?? usageFrom(nested(payload, 'output', 'usage'));
}

function providerFromPayload(payload: UnknownRecord): string | undefined {
  return asString(nested(payload, 'metadata', 'providerMetadata', 'openrouter', 'provider'))
    ?? asString(nested(payload, 'metadata', 'modelMetadata', 'modelProvider'));
}

function modelFromPayload(payload: UnknownRecord): string | undefined {
  return asString(nested(payload, 'metadata', 'modelId'))
    ?? asString(nested(payload, 'metadata', 'modelMetadata', 'modelId'))
    ?? asString(nested(payload, 'request', 'body', 'model'));
}

function reasoningFromPayload(payload: UnknownRecord): string | undefined {
  const details = nested(payload, 'metadata', 'providerMetadata', 'openrouter', 'reasoning_details');
  if (!Array.isArray(details)) return undefined;
  const text = details
    .map((item) => asString(asRecord(item).text) ?? '')
    .filter(Boolean)
    .join('\n\n');
  return text || undefined;
}

function addTrace(
  run: RunState,
  entry: Omit<TraceEntry, 'id'>,
): RunState {
  return {
    ...run,
    trace: [...run.trace, { id: crypto.randomUUID(), ...entry }],
  };
}

function completeLatestTrace(run: RunState, kind: TraceEntry['kind'], atMs: number, detail?: string): RunState {
  const index = run.trace.findLastIndex((entry) => entry.kind === kind && entry.status === 'running');
  if (index < 0) return run;
  const trace = [...run.trace];
  const current = trace[index];
  trace[index] = {
    ...current,
    status: 'complete',
    detail: detail ?? current.detail,
    durationMs: Math.max(0, atMs - current.atMs),
  };
  return { ...run, trace };
}

function upsertTool(run: RunState, id: string, patch: Partial<ToolCall>, atMs: number): RunState {
  const index = run.tools.findIndex((tool) => tool.id === id);
  const tools = [...run.tools];
  if (index >= 0) {
    tools[index] = { ...tools[index], ...patch };
  } else {
    tools.push({
      id,
      name: asString(patch.name) ?? 'tool',
      status: patch.status ?? 'preparing',
      startedAtMs: patch.startedAtMs ?? atMs,
      ...patch,
    });
  }
  return { ...run, tools };
}

function pushEvent(run: RunState, event: MastraEvent, atMs: number): RunState {
  const record: RawEventRecord = {
    sequence: run.totalEvents + 1,
    type: event.type,
    from: asString(event.from),
    runId: asString(event.runId),
    atMs,
    raw: sanitizeForDisplay(event),
  };
  return {
    ...run,
    totalEvents: run.totalEvents + 1,
    eventCounts: {
      ...run.eventCounts,
      [event.type]: (run.eventCounts[event.type] ?? 0) + 1,
    },
    eventLog: [...run.eventLog, record],
  };
}

function unwrapEvent(event: MastraEvent): {
  type: string;
  payload: UnknownRecord;
  wrapped: boolean;
  runId?: string;
} {
  if (event.type.startsWith('agent-execution-event-')) {
    const wrapper = asRecord(event.payload);
    return {
      type: asString(wrapper.type) ?? event.type.replace('agent-execution-event-', ''),
      payload: asRecord(wrapper.payload),
      wrapped: true,
      runId: asString(wrapper.runId) ?? asString(event.runId),
    };
  }
  return {
    type: event.type,
    payload: asRecord(event.payload),
    wrapped: false,
    runId: asString(event.runId),
  };
}

function handleRouting(run: RunState, event: MastraEvent, atMs: number): RunState {
  const payload = asRecord(event.payload);
  switch (event.type) {
    case 'routing-agent-start':
      return addTrace(run, {
        kind: 'routing',
        label: 'Lead is planning',
        detail: asString(nested(payload, 'inputData', 'task')),
        status: 'running',
        atMs,
      });
    case 'routing-agent-end': {
      const primitiveId = asString(payload.primitiveId);
      const primitiveType = asString(payload.primitiveType);
      const isComplete = typeof payload.isComplete === 'boolean' ? payload.isComplete : undefined;
      const route = {
        primitiveId,
        primitiveType,
        prompt: asString(payload.prompt),
        rationale: asString(payload.selectionReason) ?? asString(payload.result),
        isComplete,
        usage: usageFrom(payload.usage),
      };
      let next: RunState = { ...run, route };
      next = completeLatestTrace(
        next,
        'routing',
        atMs,
        isComplete ? 'Lead answered directly' : `Delegated to ${primitiveId ?? primitiveType ?? 'agent'}`,
      );
      return next;
    }
    case 'routing-agent-text-start':
      return addTrace(run, {
        kind: 'response',
        label: 'Composing final answer',
        status: 'running',
        atMs,
      });
    case 'routing-agent-text-delta':
      return { ...run, answer: run.answer + (asString(payload.text) ?? asString(payload.delta) ?? '') };
    case 'agent-execution-start': {
      const agent = asString(payload.agentId) ?? run.route?.primitiveId ?? 'delegated agent';
      return addTrace(run, {
        kind: 'agent',
        label: `${agent} started`,
        detail: asString(payload.task),
        status: 'running',
        atMs,
      });
    }
    case 'agent-execution-end':
      return completeLatestTrace(run, 'agent', atMs, 'Agent returned control to Lead');
    case 'network-validation-start':
      return addTrace(run, {
        kind: 'validation',
        label: 'Validating result',
        detail: asNumber(payload.checksCount) !== undefined ? `${asNumber(payload.checksCount)} check` : undefined,
        status: 'running',
        atMs,
      });
    case 'network-validation-end': {
      const results = Array.isArray(payload.results) ? payload.results.map(asRecord) : [];
      const first = results[0] ?? {};
      const passed = typeof payload.passed === 'boolean' ? payload.passed : undefined;
      let next: RunState = {
        ...run,
        validation: {
          passed,
          reason: asString(payload.reason) ?? asString(first.reason),
          durationMs: asNumber(payload.duration),
          score: asNumber(first.score),
          timedOut: typeof payload.timedOut === 'boolean' ? payload.timedOut : undefined,
        },
      };
      next = completeLatestTrace(next, 'validation', atMs, passed === false ? 'Validation failed' : 'Validation passed');
      return next;
    }
    case 'network-execution-event-finish': {
      let next: RunState = {
        ...run,
        status: 'complete',
        endedAt: run.startedAt + atMs,
        answer: asString(payload.result) ?? run.answer,
        finishReason: asString(payload.completionReason),
        totalUsage: usageFrom(payload.usage) ?? run.totalUsage,
      };
      next = completeLatestTrace(next, 'response', atMs);
      return addTrace(next, {
        kind: 'system',
        label: 'Run complete',
        detail: asString(payload.completionReason),
        status: 'complete',
        atMs,
      });
    }
    case 'network-execution-event-step-start':
    case 'network-execution-event-iteration-start':
      return addTrace(run, {
        kind: 'system',
        label: 'Network iteration',
        detail: asNumber(payload.iteration) !== undefined ? `Iteration ${asNumber(payload.iteration)! + 1}` : undefined,
        status: 'info',
        atMs,
      });
    default:
      return run;
  }
}

function handleStreamEvent(
  run: RunState,
  type: string,
  payload: UnknownRecord,
  wrapped: boolean,
  runId: string | undefined,
  atMs: number,
): RunState {
  switch (type) {
    case 'start':
      return wrapped
        ? addTrace(run, { kind: 'agent', label: `${run.route?.primitiveId ?? 'Agent'} stream opened`, status: 'info', atMs })
        : run;
    case 'step-start': {
      const index = run.steps.length + 1;
      const stepId = runId ? `${runId}:${index}` : `${run.id}:step:${index}`;
      const step: ModelStep = {
        id: stepId,
        index,
        status: 'running',
        startedAtMs: atMs,
        model: modelFromPayload(payload),
        maxOutputTokens: asNumber(nested(payload, 'request', 'body', 'max_tokens')),
        warnings: stringArray(payload.warnings),
      };
      return addTrace(
        { ...run, steps: [...run.steps, step], activeStepId: stepId },
        {
          kind: 'step',
          label: `Model step ${index}`,
          detail: step.model,
          status: 'running',
          atMs,
        },
      );
    }
    case 'reasoning-start': {
      const next = run.reasoning && !run.reasoning.endsWith('\n\n')
        ? { ...run, reasoning: `${run.reasoning.trimEnd()}\n\n` }
        : run;
      return addTrace(next, {
        kind: 'reasoning',
        label: 'Reasoning',
        detail: `Step ${run.steps.length || 1}`,
        status: 'running',
        atMs,
      });
    }
    case 'reasoning-delta':
      return { ...run, reasoning: run.reasoning + (asString(payload.text) ?? asString(payload.delta) ?? '') };
    case 'reasoning-end':
      return completeLatestTrace(run, 'reasoning', atMs);
    case 'text-start':
      return addTrace(run, {
        kind: 'response',
        label: wrapped ? `${run.route?.primitiveId ?? 'Agent'} is answering` : 'Writing answer',
        status: 'running',
        atMs,
      });
    case 'text-delta':
      return { ...run, answer: run.answer + (asString(payload.text) ?? asString(payload.delta) ?? '') };
    case 'text-end':
      return completeLatestTrace(run, 'response', atMs);
    case 'tool-call-input-streaming-start': {
      const id = asString(payload.toolCallId) ?? crypto.randomUUID();
      const name = asString(payload.toolName) ?? 'tool';
      return addTrace(
        upsertTool(run, id, { name, status: 'preparing', startedAtMs: atMs }, atMs),
        { kind: 'tool', label: `Preparing ${name}`, status: 'running', atMs },
      );
    }
    case 'tool-call-delta': {
      const id = asString(payload.toolCallId);
      if (!id) return run;
      const tool = run.tools.find((item) => item.id === id);
      return upsertTool(run, id, {
        argsText: `${tool?.argsText ?? ''}${asString(payload.argsTextDelta) ?? ''}`,
      }, atMs);
    }
    case 'tool-call-input-streaming-end':
      return run;
    case 'tool-call': {
      const id = asString(payload.toolCallId) ?? crypto.randomUUID();
      const name = asString(payload.toolName) ?? run.tools.find((tool) => tool.id === id)?.name ?? 'tool';
      let next = upsertTool(run, id, {
        name,
        args: sanitizeForDisplay(payload.args ?? payload.input),
        status: 'running',
      }, atMs);
      next = completeLatestTrace(next, 'tool', atMs, `${name} called`);
      return addTrace(next, {
        kind: 'tool',
        label: name,
        detail: 'Tool running',
        status: 'running',
        atMs,
      });
    }
    case 'tool-result': {
      const id = asString(payload.toolCallId);
      if (!id) return run;
      const result = sanitizeForDisplay(payload.result ?? payload.output);
      let next = upsertTool(run, id, { result, status: 'complete', endedAtMs: atMs }, atMs);
      next = completeLatestTrace(next, 'tool', atMs, 'Result received');
      return next;
    }
    case 'step-finish': {
      const activeId = run.activeStepId ?? run.steps.at(-1)?.id;
      const steps = run.steps.map((step) => step.id === activeId ? {
        ...step,
        status: 'complete' as const,
        endedAtMs: atMs,
        model: modelFromPayload(payload) ?? step.model,
        provider: providerFromPayload(payload) ?? step.provider,
        finishReason: asString(nested(payload, 'stepResult', 'reason')),
        usage: usageFromPayload(payload) ?? step.usage,
        warnings: [...step.warnings, ...stringArray(nested(payload, 'stepResult', 'warnings'))],
      } : step);
      const metadataReasoning = reasoningFromPayload(payload);
      const reasoning = metadataReasoning && !run.reasoning.includes(metadataReasoning)
        ? `${run.reasoning.trimEnd()}${run.reasoning ? '\n\n' : ''}${metadataReasoning}`
        : run.reasoning;
      let next: RunState = { ...run, steps, reasoning, activeStepId: undefined };
      next = completeLatestTrace(next, 'step', atMs, providerFromPayload(payload) ?? modelFromPayload(payload));
      const outputText = asString(nested(payload, 'output', 'text'));
      return outputText && !wrapped ? { ...next, answer: outputText } : next;
    }
    case 'finish': {
      const outputText = asString(nested(payload, 'output', 'text'));
      if (wrapped) {
        return addTrace(
          { ...run, answer: outputText ?? run.answer },
          { kind: 'agent', label: `${run.route?.primitiveId ?? 'Agent'} finished`, status: 'complete', atMs },
        );
      }
      let next: RunState = {
        ...run,
        status: 'complete',
        endedAt: run.startedAt + atMs,
        answer: outputText ?? run.answer,
        finishReason: asString(nested(payload, 'stepResult', 'reason')),
        totalUsage: usageFromPayload(payload) ?? run.totalUsage,
      };
      next = completeLatestTrace(next, 'response', atMs);
      return addTrace(next, { kind: 'system', label: 'Run complete', status: 'complete', atMs });
    }
    case 'error': {
      const message = asString(payload.message) ?? asString(payload.error) ?? 'Mastra stream returned an error';
      return addTrace(
        { ...run, status: 'error', error: message, endedAt: run.startedAt + atMs },
        { kind: 'system', label: 'Run failed', detail: message, status: 'error', atMs },
      );
    }
    default:
      return run;
  }
}

export function reduceRunEvent(current: RunState, event: MastraEvent, receivedAt = Date.now()): RunState {
  const atMs = Math.max(0, receivedAt - current.startedAt);
  let run = pushEvent(current, event, atMs);
  run = handleRouting(run, event, atMs);
  const normalized = unwrapEvent(event);
  run = handleStreamEvent(run, normalized.type, normalized.payload, normalized.wrapped, normalized.runId, atMs);
  return run;
}

export function finishRunWithError(run: RunState, error: unknown, status: 'error' | 'aborted' = 'error'): RunState {
  const message = error instanceof Error ? error.message : String(error);
  const atMs = Math.max(0, Date.now() - run.startedAt);
  return addTrace(
    {
      ...run,
      status,
      endedAt: Date.now(),
      error: status === 'aborted' ? undefined : message,
    },
    {
      kind: 'system',
      label: status === 'aborted' ? 'Run stopped' : 'Run failed',
      detail: status === 'aborted' ? 'Stopped by user' : message,
      status: status === 'aborted' ? 'info' : 'error',
      atMs,
    },
  );
}

export function finishRunAfterStream(run: RunState): RunState {
  if (run.status !== 'streaming') return run;
  const atMs = Math.max(0, Date.now() - run.startedAt);
  const next = completeLatestTrace(
    {
      ...run,
      status: 'complete',
      endedAt: Date.now(),
    },
    'response',
    atMs,
  );
  return addTrace(next, {
    kind: 'system',
    label: 'Stream closed',
    detail: run.answer ? 'Response stream completed' : 'No answer text was returned',
    status: 'complete',
    atMs,
  });
}

export function sumStepUsage(steps: ModelStep[]): UsageMetrics | undefined {
  const usages = steps.map((step) => step.usage).filter((usage): usage is UsageMetrics => Boolean(usage));
  if (usages.length === 0) return undefined;
  const sum = (key: keyof UsageMetrics) => {
    const values = usages.map((usage) => usage[key]).filter((value): value is number => typeof value === 'number');
    return values.length ? values.reduce((total, value) => total + value, 0) : undefined;
  };
  return {
    inputTokens: sum('inputTokens'),
    outputTokens: sum('outputTokens'),
    totalTokens: sum('totalTokens'),
    reasoningTokens: sum('reasoningTokens'),
    cachedInputTokens: sum('cachedInputTokens'),
    cacheCreationInputTokens: sum('cacheCreationInputTokens'),
    cost: sum('cost'),
  };
}
